import React, { useState, useEffect, useRef } from 'react';

const Excerpts = () => {
  const [pdfLoading, setPdfLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.5);
  const [pdfDoc, setPdfDoc] = useState(null);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  useEffect(() => {
    loadPdfJs();
  }, []);

  useEffect(() => {
    if (pdfDoc && currentPage) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage, scale]);

  const loadPdfJs = () => {
    if (window.pdfjsLib) {
      loadPdf();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      loadPdf();
    };
    document.body.appendChild(script);
  };

  const loadPdf = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/book/preview-pdf`);
      const arrayBuffer = await response.arrayBuffer();
      const loadingTask = window.pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setPdfLoading(false);
    } catch (error) {
      console.error('Error loading preview PDF:', error);
      setPdfLoading(false);
    }
  };

  const renderPage = async (pageNum) => {
    if (!pdfDoc || !canvasRef.current) return;

    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch (e) {}
    }

    try {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.height = viewport.height;
      canvas.width = viewport.width;
      context.clearRect(0, 0, canvas.width, canvas.height);

      renderTaskRef.current = page.render({
        canvasContext: context,
        viewport: viewport,
      });
      await renderTaskRef.current.promise;
    } catch (error) {
      if (error.name !== 'RenderingCancelledException') {
        console.error('Error rendering page:', error);
      }
    }
  };

  const goToPage = (page) => {
    if (numPages && page >= 1 && page <= numPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="min-h-screen py-24 md:py-32 px-6 md:px-12">
      <div className="container mx-auto max-w-4xl">
        <h1 className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-charcoal mb-8 text-center">
          Uittreksels uit die Boek
        </h1>
        <p className="text-center font-ui text-sm uppercase tracking-widest text-muted-charcoal mb-16">
          Lees 'n Voorskou van die Werk
        </p>

        <div className="space-y-16">
          {/* Excerpt 1 */}
          <div data-testid="excerpt-1" className="bg-parchment border border-[#E5E0D6] p-8 md:p-12">
            <h2 className="font-heading text-2xl md:text-3xl font-medium italic mb-6 text-charcoal">
              Uit die Inleiding
            </h2>
            <div className="font-body text-lg leading-loose text-charcoal space-y-4">
              <p>
                In Eksodus 32 lees ons van die volk Israel wat, terwyl Moses op die berg was 
                om die wet van God te ontvang, 'n goue kalf gemaak het. Hulle het gesê: 
                "Dit is jou gode, Israel, wat jou uit Egipte uitgelei het."
              </p>
              <p>
                Hierdie beeld was nie bedoel as 'n vervanging vir YHWH nie, maar as 'n 
                "verteenwoordiging" van Hom. Dit was 'n poging om die onsienlike God 
                sigbaar te maak. Maar God het dit as afgodery beskou, en duisende het 
                gesterf as gevolg van hierdie sonde.
              </p>
              <p>
                Vandag, duisende jare later, het ons ook ons eie "goue kalwers" opgerig — 
                nie van gesmelte goud nie, maar van klip, brons, en nasionale simboliek.
              </p>
              <p className="italic text-muted-charcoal">
                [Lees verder in die volledige publikasie.]
              </p>
            </div>
          </div>

          {/* Excerpt 2 */}
          <div data-testid="excerpt-2" className="bg-parchment border border-[#E5E0D6] p-8 md:p-12">
            <h2 className="font-heading text-2xl md:text-3xl font-medium italic mb-6 text-charcoal">
              Uit Hoofstuk 4: Geheimenisse van die Voortrekker Monument
            </h2>
            <div className="font-body text-lg leading-loose text-charcoal space-y-4">
              <p>
                Die Voortrekker Monument is nie net 'n gedenkteken vir die pioniers nie — 
                dit is 'n tempel vol verborge simboliek. Van die Egiptiese godin Hathor 
                tot die bul-god Apis, van die alsiende oog tot die Shiva Lingam, elke 
                element van hierdie gebou dra 'n donker geestelike betekenis.
              </p>
              <p>
                Die argitek, Gerhard Moerdijk, het sy inspirasie gekry van die Duitse 
                Völkerschlachtdenkmal — 'n monument wat self gebaseer is op heidense 
                Egiptiese argitektuur. Hy het doelbewus hierdie simbole ingebou, en 
                duisende Afrikaners buig vandag steeds voor hierdie beeld.
              </p>
              <p className="italic text-muted-charcoal">
                [Die volledige hoofstuk bevat foto's en gedetailleerde analises van elke simbool.]
              </p>
            </div>
          </div>

          {/* PDF Preview Viewer */}
          <div data-testid="excerpt-pdf" className="bg-surface/30 border border-[#E5E0D6] p-8 md:p-12">
            <h3 className="font-heading text-xl md:text-2xl font-medium italic mb-4 text-charcoal text-center">
              Lees 'n Uittreksel uit die Boek
            </h3>
            <p className="font-body text-lg leading-loose text-muted-charcoal mb-8 text-center">
              Blaai deur 'n voorskou van die boek
            </p>
            
            {pdfLoading ? (
              <div className="bg-white border border-[#E5E0D6] h-96 flex items-center justify-center">
                <p className="font-body text-lg text-muted-charcoal">PDF laai...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                {/* PDF Controls */}
                <div className="flex items-center gap-4 mb-4">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border border-charcoal hover:bg-charcoal hover:text-parchment text-charcoal rounded-sm px-4 py-2 font-ui uppercase tracking-widest text-xs transition-colors disabled:opacity-30"
                  >
                    ← Vorige
                  </button>
                  <span className="font-ui text-sm text-charcoal">
                    Bladsy {currentPage} / {numPages || '...'}
                  </span>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === numPages}
                    className="border border-charcoal hover:bg-charcoal hover:text-parchment text-charcoal rounded-sm px-4 py-2 font-ui uppercase tracking-widest text-xs transition-colors disabled:opacity-30"
                  >
                    Volgende →
                  </button>
                </div>
                
                {/* Zoom Controls */}
                <div className="flex items-center gap-2 mb-6">
                  <button
                    onClick={() => setScale(Math.max(0.8, scale - 0.2))}
                    className="text-charcoal hover:text-gold font-ui text-xs px-2"
                  >
                    A-
                  </button>
                  <button
                    onClick={() => setScale(1.2)}
                    className="text-charcoal hover:text-gold font-ui text-xs px-2"
                  >
                    100%
                  </button>
                  <button
                    onClick={() => setScale(Math.min(2, scale + 0.2))}
                    className="text-charcoal hover:text-gold font-ui text-xs px-2"
                  >
                    A+
                  </button>
                </div>
                
                {/* PDF Canvas */}
                <div className="bg-white border border-[#E5E0D6] overflow-auto" style={{ maxHeight: '85vh', minHeight: '700px' }}>
                  <canvas ref={canvasRef} className="max-w-full" />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="font-body text-xl leading-relaxed text-charcoal mb-8">
            Wil u die volledige boek lees?
          </p>
          <a
            href="/purchase"
            data-testid="cta-purchase-from-excerpts"
            className="inline-block bg-charcoal text-parchment hover:bg-charcoal/80 rounded-sm px-8 py-4 font-ui uppercase tracking-widest text-sm transition-colors duration-300"
          >
            Skaf die Boek aan
          </a>
        </div>
      </div>
    </div>
  );
};

export default Excerpts;
